import { TriangleAlert } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";

import { OrderFormData } from "./model";
import { OrderForm } from "./ui";

import {
  useGetUserProfile,
  useCreateCargoOrder,
  OrderCreateDto,
  UnloadingDirectionDTOTariffRoad,
  UnloadingDirectionDTOTariffStation,
  useGetCargoOrder,
  useCreateCargoDraft,
  DraftOrderDto,
} from "@/common/api";
import { Alert, AlertTitle, AlertDescription } from "@/common/components";
import LoadingModal from "@/common/components/ui/loading-modal";
import { ROUTES } from "@/common/config";
import { useUser } from "@/common/hooks";
import { useAuth } from "@/common/hooks/useAuth";
import { useToast } from "@/common/hooks/useToast";

interface ManageOrderPageProps {
  orderId?: string;
}

export function ManageOrderPage({ orderId }: ManageOrderPageProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const {
    auth: { id, isWagonOwner },
  } = useAuth();
  const { user } = useUser();

  const [commentModerator, setCommentModerator] = useState<string>();

  const { data: orderData, isLoading: isOrderLoading } = useGetCargoOrder(
    { id: orderId!, userId: user?.id! },
    { query: { enabled: Boolean(orderId) } },
  );

  const { data: profileData, isLoading: isProfileLoading } = useGetUserProfile({
    userId: id!,
  });

  const { mutateAsync: mutateOrderAsync, isPending } = useCreateCargoOrder();
  const { mutateAsync: mutateDraftAsync } = useCreateCargoDraft();

  const handleSubmit = (form: OrderFormData) => {
    const {
      customerName,
      customerInn,
      createdDate,
      wagonCode,
      wagonCarrying,
      wagonVolume,
      unloadingDirection,
      deadlineAt,
      deadlineTo,
      collectionBegin,
      collectionEnd,
      tradeBegin,
      tradeEnd,
      retradingBegin,
      retradingEnd,
      resultsDate,
      ...rest
    } = form;

    const data: OrderCreateDto = {
      id: orderId,
      ...rest,
      userId: id!,
      modelId: wagonCode ?? "",
      loadingStation: form.loadingStation,
      loadingRoad: form.loadingRoad,
      totalWagons: Number(form.totalWagons),
      cargoWeight: form.cargoWeight ?? 0,
      unloadingDirection: form.unloadingDirection?.map((item, index) => ({
        unloadingStation: item.unloadingStation,
        unloadingRoad: item.unloadingRoad,
        tariffStation: item.tariffStation,
        tariffRoad: item.tariffStation,
        countWagons: Number(item.countWagons),
        indexNumber: index,
      })),
      deadlineAt: deadlineAt ?? "",
      deadlineTo: deadlineTo ?? "",
      collectionBegin: new Date(collectionBegin).toISOString(),
      collectionEnd: new Date(collectionEnd).toISOString(),
      tradeBegin: new Date(tradeBegin).toISOString(),
      tradeEnd: new Date(tradeEnd).toISOString(),
      retradingBegin: form.retrading ? new Date(retradingBegin).toISOString() : "",
      retradingEnd: form.retrading ? new Date(retradingEnd).toISOString() : "",
      resultsDate: form.resultsDate ? new Date(resultsDate).toISOString() : "",
      liftingCapacity: wagonCarrying,
      bodySize: wagonVolume,
    };

    mutateOrderAsync({
      data,
    })
      .then(() => {
        toast({
          title: "Заявка отправлена на проверку администратору",
        });
        setLocation(ROUTES.ROOT);
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка создания заявки",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при создании заявки",
        });
      });
  };

  const handleDraft = (form: OrderFormData) => {
    const {
      customerName,
      customerInn,
      createdDate,
      wagonCode,
      wagonCarrying,
      wagonVolume,
      unloadingDirection,
      deadlineAt,
      deadlineTo,
      collectionBegin,
      collectionEnd,
      tradeBegin,
      tradeEnd,
      retradingBegin,
      retradingEnd,
      resultsDate,
      ...rest
    } = form;

    const data: DraftOrderDto = {
      ...rest,
      id: orderId,
      userId: id!,
      modelId: wagonCode ?? "",
      loadingStation: form.loadingStation,
      loadingRoad: form.loadingRoad,
      totalWagons: Number(form.totalWagons),
      unloadingDirection: form.unloadingDirection?.map((item, index) => ({
        unloadingStation: item.unloadingStation,
        unloadingRoad: item.unloadingRoad,
        tariffStation: item.tariffStation,
        tariffRoad: item.tariffStation as UnloadingDirectionDTOTariffRoad,
        countWagons: Number(item.countWagons),
        indexNumber: index,
      })),
      deadlineAt: deadlineAt ?? "",
      deadlineTo: deadlineTo ?? "",
      collectionBegin: collectionBegin ? new Date(collectionBegin).toISOString() : "",
      collectionEnd: collectionEnd ? new Date(collectionEnd).toISOString() : "",
      tradeBegin: tradeBegin ? new Date(tradeBegin).toISOString() : "",
      tradeEnd: tradeEnd ? new Date(tradeEnd).toISOString() : "",
      retradingBegin:
        form.retrading && retradingBegin ? new Date(retradingBegin).toISOString() : "",
      retradingEnd: form.retrading && retradingEnd ? new Date(retradingEnd).toISOString() : "",
      resultsDate: form.resultsDate ? new Date(resultsDate).toISOString() : "",
      liftingCapacity: wagonCarrying,
      bodySize: wagonVolume,
    };

    mutateDraftAsync({
      data,
    })
      .then(() => {
        toast({
          title: "Черновик сохранен",
        });
        setLocation(ROUTES.DRAFTS);
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка создания черновика",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при создании черновика",
        });
      });
  };

  const handleCancel = () => {
    setLocation(ROUTES.ROOT);
  };

  useEffect(() => {
    if (orderData?.data.payload) {
      setCommentModerator(orderData?.data.payload.moderationComment);
    }
  }, [orderData?.data.payload]);

  if (isOrderLoading || isProfileLoading) {
    return <LoadingModal isOpen={true} />;
  }

  if (!profileData?.data.payload) {
    return null;
  }

  return (
    <div className="container mx-auto md:py-6">
      <div className="mb-6 flex flex-col md:flex-row gap-5 justify-between">
        <div>
          <h1 className="text-Main-orange text-xl md:text-2xl font-bold uppercase leading-6 md:leading-8">
            {isWagonOwner ? "Создать заявку-предложение подвижного состава" : "Создать заявку"}
          </h1>

          <p className="text-Text-secondary text-base font-normal leading-5 mt-2">
            {isWagonOwner
              ? "Заполните форму для создания заявки-предложения подвижного состава"
              : "Заполните форму для создания новой заявки на перевозку груза"}
          </p>
        </div>
        {commentModerator && (
          <Alert variant="destructive" className="w-full md:w-1/2 bg-red-100 border-red-800">
            <TriangleAlert className="size-4" />
            <AlertTitle className="text-red-800 text-sm">Комментарий модератора:</AlertTitle>
            <AlertDescription className="text-xs">{commentModerator}</AlertDescription>
          </Alert>
        )}
      </div>

      <OrderForm
        isPending={isPending}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDraft={handleDraft}
        profileData={profileData.data.payload}
        orderData={orderData?.data.payload}
      />
    </div>
  );
}
